import { Controller } from "@hotwired/stimulus"
import ClipBuilderApi from "../services/clip_builder_api"
import { pollPreviewStatus } from "../services/preview_poller"
import {
  renderPickerListMarkup,
  renderPickerErrorMarkup,
  modelPickerMarkup,
  removeVideoConfirmMarkup,
} from "../utils/clip_builder_markup"

/**
 * Clip Video Source Controller
 *
 * Everything that produces or swaps a clip's video output: single-clip preview
 * render (+ status polling), the "Use render" picker, generate-via-Veo (model
 * picker popover), and video removal. Extracted from the clip_builder
 * monolith. Requests go through ClipBuilderApi; the polling state machine
 * lives in the preview_poller service; the small dynamic popovers come from
 * clip_builder_markup utils.
 */
export default class extends Controller {
  static values = {
    previewUrl: String,
    previewStatusUrl: String,
    availableRendersUrl: String,
    selectRenderUrl: String,
    generateUrl: String,
    updateUrl: String,
    segmentIndex: Number,
  }
  static targets = ["renderPicker", "renderPickerLoading", "previewButton"]

  get api() {
    return new ClipBuilderApi({
      previewUrl: this.previewUrlValue,
      previewStatusUrl: this.previewStatusUrlValue,
      availableRendersUrl: this.availableRendersUrlValue,
      selectRenderUrl: this.selectRenderUrlValue,
      generateUrl: this.generateUrlValue,
      updateUrl: this.updateUrlValue,
      segmentIndex: this.segmentIndexValue,
    })
  }

  // --- Single-clip preview ---

  async previewClip() {
    if (!this.previewUrlValue) return

    const btn = this.hasPreviewButtonTarget ? this.previewButtonTarget : null
    if (btn) {
      btn.textContent = "Rendering..."
      btn.classList.add("text-yellow-600")
      btn.classList.remove("text-gray-500")
      btn.disabled = true
    }

    try {
      const response = await this.api.startPreview()
      const data = await response.json()
      if (!response.ok || !data.render_job_id) {
        this._failPreview(btn)
        return
      }

      pollPreviewStatus({
        fetchStatus: async () => (await this.api.previewStatus(data.render_job_id)).json(),
        onComplete: (videoUrl) => this._previewReady(btn, videoUrl),
        onFailed: () => this._failPreview(btn),
        onTimeout: () => { if (btn) btn.textContent = "Preview timed out" },
        onError: () => { if (btn) btn.textContent = "Preview error" },
      })
    } catch {
      this._failPreview(btn)
    }
  }

  _previewReady(btn, videoUrl) {
    if (!btn) return
    btn.textContent = "▶ View Preview"
    btn.classList.remove("text-yellow-600")
    btn.classList.add("text-green-600", "cursor-pointer")
    btn.disabled = false
    btn.removeAttribute("data-action")
    btn.addEventListener("click", (e) => {
      e.preventDefault()
      window.open(videoUrl, "_blank")
    })
  }

  _failPreview(btn) {
    if (!btn) return
    btn.textContent = "Preview failed"
    btn.classList.remove("text-yellow-600")
    btn.classList.add("text-red-500")
  }

  // --- Use an existing completed render ---

  async showRenderPicker() {
    if (!this.hasRenderPickerTarget) return

    const picker = this.renderPickerTarget
    if (!picker.classList.contains("hidden")) {
      picker.classList.add("hidden")
      return
    }
    picker.classList.remove("hidden")

    try {
      const response = await this.api.fetchRenders()
      const data = await response.json()
      picker.innerHTML = renderPickerListMarkup(data.renders)
    } catch {
      picker.innerHTML = renderPickerErrorMarkup()
    }
  }

  async selectRender(event) {
    const renderId = event.currentTarget.dataset.renderId
    if (!renderId) return

    try {
      const response = await this.api.selectRender(renderId)
      if (response.ok) {
        window.location.reload()
      } else {
        const data = await response.json().catch(() => ({}))
        alert(data.error || "Failed to select render")
      }
    } catch {
      alert("Failed to select render")
    }
  }

  // --- Remove video (inline confirm) ---

  removeVideo(event) {
    const btn = event.currentTarget
    const existing = this.element.querySelector(".remove-confirm")
    if (existing) { existing.remove(); return }

    const confirm = document.createElement("span")
    confirm.className = "remove-confirm inline-flex items-center gap-1 ml-1"
    confirm.innerHTML = removeVideoConfirmMarkup()
    btn.parentNode.insertBefore(confirm, btn.nextSibling)

    confirm.querySelector("[data-no]").addEventListener("click", () => confirm.remove())
    confirm.querySelector("[data-yes]").addEventListener("click", async () => {
      try {
        await this.api.updateSegment({
          video: null,
          video_duration_seconds: null,
          video_generation_status: null,
          video_generation_error: null,
        })
        window.location.reload()
      } catch {
        // Silent fail
      }
    })
  }

  // --- Generate video via Veo (model picker popover) ---

  generateVideo(event) {
    const btn = event.currentTarget
    const existing = this.element.querySelector(".model-picker")
    if (existing) { existing.remove(); return }

    const picker = document.createElement("div")
    picker.className = "model-picker absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-2 space-y-1"
    picker.style.cssText = "right: 0; bottom: 100%; margin-bottom: 4px; min-width: 200px;"
    picker.innerHTML = modelPickerMarkup()

    const row = btn.closest(".flex")
    row.style.position = "relative"
    row.appendChild(picker)

    picker.querySelectorAll("[data-model]").forEach((opt) => {
      opt.addEventListener("click", () => {
        picker.remove()
        this._submitGenerateVideo(opt.dataset.model)
      })
    })

    const closeHandler = (e) => {
      if (!picker.contains(e.target) && e.target !== btn) {
        picker.remove()
        document.removeEventListener("click", closeHandler)
      }
    }
    setTimeout(() => document.addEventListener("click", closeHandler), 0)
  }

  async _submitGenerateVideo(modelKey) {
    try {
      const response = await this.api.generateVideo(modelKey)
      const data = await response.json()
      if (response.ok) {
        window.location.reload()
      } else {
        alert(data.error || "Failed to start video generation")
      }
    } catch {
      alert("Failed to start video generation")
    }
  }
}
